import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, Tooltip, OverlayTrigger } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import { useSignalR } from "../../../contexts/SignalRContext";
import { SOLD_TO_SHIP_TO_CHANGED } from "../../../config/liveUpdateEvents";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { setSoldToShipToDetails } from "../../../redux/slices/psnSlice";
import { setJobDetail } from "../../../redux/slices/jobDetailsSlice";
import { selectSoldToShipTo, selectFieldDescriptions, selectPriceBreakdown } from "../../../redux/selectors";
import useJobHandler from "../../../functions/JobHandler"; // Import useJobHandler function
import InternationalShip from "./InternationalShipping"
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import useFieldClass from "../../../functions/useFieldClass";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";


function SoldToShipTo({ zone }) {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "soldToShipTo";

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const priceBreakdownSectionName = "priceBreakdown";
  const priceBreakdownApiUrl = apiUrls[priceBreakdownSectionName];
  const priceBreakdownTableKey = priceBreakdownSectionName;
  // Restore original apiUrl and tableKey for other fields
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux


  const storedJobDetails = useSelector(selectSoldToShipTo);
  const storedPriceBreakdown = useSelector(selectPriceBreakdown); // Get PriceBreakdown from jobDetails slice

  // Use SellPriceAmount from PriceBreakdown for InvoicePrice
  const jobDetails = useMemo(() => {
    const details = storedJobDetails[0] || {};
    const priceBreakdownDetails = storedPriceBreakdown[0] || {};
    return {
      ...details,
      InvoicePrice: priceBreakdownDetails.SellPriceAmount || "",
    };
  }, [storedJobDetails, storedPriceBreakdown]);

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const error = null;

  const { formData, handleChange, setFormData } = useFormState({}); // Form state management

  useJobHandler(); // Call useJobHandler function

  useEffect(() => {
    if (jobDetails) {
      dispatch(setSoldToShipToDetails(jobDetails));
      setFormData({
        ID: jobDetails.ID || "",
        sellPriceAmount: jobDetails.InvoicePrice || "",
        arrivalConfirmationNotes: jobDetails.ArrivalConfirmationNotes || "",
        carrier: jobDetails.Carrier || "",
        customerCarrierCallAhead: jobDetails.CustomerCarrierCallAhead || "",
        grossMarginPercent: jobDetails.GrossMarginPercent || "",
        installationServiceType: jobDetails.InstallationServiceType || "",
        internationalShip: jobDetails.InternationalShip || "",
        notificationOfShipment: jobDetails.NotificationOfShipment || "",
        paymentTerms: jobDetails.PaymentTerms || "",
        selectJobSiteServicesIncluded:
          jobDetails.SelectJobSiteServicesIncluded || "",
        shipmentDetailsOther: jobDetails.ShipmentDetailsOther || "",
        shipToAddress: jobDetails.ShipToAddress || "",
        shipToAddress2: jobDetails.ShipToAddress2 || "",
        shipToCity: jobDetails.ShipToCity || "",
        shipToCompanyName: jobDetails.ShipToCompanyName || "",
        shipToCountry: jobDetails.ShipToCountry || "",
        shipToEmail: jobDetails.ShipToEmail || "",
        shipToFax: jobDetails.ShipToFax || "",
        shipToFirstName: jobDetails.ShipToFirstName || "",
        shipToLastName: jobDetails.ShipToLastName || "",
        shipToMobilePhone: jobDetails.ShipToMobilePhone || "",
        shipToPhone: jobDetails.ShipToPhone || "",
        shipToState: jobDetails.ShipToState || "",
        shipToZipCode: jobDetails.ShipToZipCode || "",
        soldToAddress: jobDetails.SoldToAddress || "",
        soldToAddress2: jobDetails.SoldToAddress2 || "",
        soldToCity: jobDetails.SoldToCity || "",
        soldToCompanyName: jobDetails.SoldToCompanyName || "",
        soldToCountry: jobDetails.SoldToCountry || "",
        soldToCustomerPO: jobDetails.SoldToCustomerPO || "",
        soldToEmail: jobDetails.SoldToEmail || "",
        soldToFax: jobDetails.SoldToFax || "",
        soldToFirstName: jobDetails.SoldToFirstName || "",
        soldToLastName: jobDetails.SoldToLastName || "",
        soldToMobilePhone: jobDetails.SoldToMobilePhone || "",
        soldToPhone: jobDetails.SoldToPhone || "",
        soldToState: jobDetails.SoldToState || "",
        soldToZipCode: jobDetails.SoldToZipCode || "",
        specialTermsApprovedBy: jobDetails.SpecialTermsApprovedBy || "",
        specialTermsNotes: jobDetails.SpecialTermsNotes || "",
        stationWeight: jobDetails.StationWeight || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  useEffect(() => {
    if (formData && formData.ID) {
      const { internationalShip } = formData;
      dispatch(setJobDetail({ internationalShip }));
    }
  }, [formData, dispatch]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { publish } = useSignalR();
  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData, null, { onSaved: async ({ projectId, tableKey: savedTable, url }) => {
      if (savedTable !== sectionName || !Number.isSafeInteger(Number(projectId)) || Number(projectId) <= 0) return;
      await publish({ type: SOLD_TO_SHIP_TO_CHANGED,
        payload: { version: 1, scope: url, projectId: Number(projectId) } });
    } });

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  /****************Add dropdown options here*************************/

  const specialTermsApprovedByOptions =
    options
      ?.filter((option) => option.DropdownType === "Special Terms Approved By")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const carrierOptions =
    options
      ?.filter((option) => option.DropdownType === "Carrier")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const genericOptions =
    options
      ?.filter((option) => option.DropdownType === "Generic Yes/ No")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const selectJobSiteServicesIncludedOptions =
    options
      ?.filter(
        (option) => option.DropdownType === "Select Job Site Services Included"
      )
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const installationServiceTypeOptions =
    options
      ?.filter((option) => option.DropdownType === "Installation Service Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const notificationOfShipmentOptions =
    options
      ?.filter((option) => option.DropdownType === "Notification Of Shipment")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const formatAsCurrency = (value) => {
    if (value || value === 0) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(value);
    }
    return value;
  };

  const [editingField, setEditingField] = React.useState(null);

  const sanitizeInput = (input) => {
    // Remove any characters that are not digits, decimal points, or minus signs
    return input.replace(/[^0-9.-]/g, "");
  };

  const handleCurrencyBlur = (
    e,
    handleBlur,
    formData,
    apiUrl,
    tableKey,
    fieldName
  ) => {
    // Parse and ensure the value is a number
    const sanitizedValue = sanitizeInput(e.target.value);
    const rawValue = parseFloat(sanitizedValue);
    if (!isNaN(rawValue)) {
      // Explicitly send a number to the backend
      handleBlur(
        { ...e, target: { ...e.target, value: String(rawValue) } }, // Ensure value is a number
        fieldName,
        [],
        {
          ID: formData.ID,
          url: apiUrl,
          field: fieldName,
          tableKey,
        }
      );
    } else {
      console.warn("Invalid numeric value provided, skipping submission.");
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          {/* General Ship to Sold To */}
          <div className="sold-ship-address-contact-groups">
          <Row className="sold-ship-section-pair">
            <Col md={6} className="sold-ship-sold-address">
              <Form
                onSubmit={handleSubmit}
                className="sold-ship-form container mt-4"
              >
                <Row className="mb-3 ">
                  <h3 className="subsection-header">Sold To</h3>
                  <Col>
                    <Form.Group as={Row} controlId="soldToCustomerPO">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToCustomerPO")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Customer PO</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToCustomerPO"
                          value={formData.soldToCustomerPO}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Customer PO", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToCustomerPO",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToCustomerPO}
                          onChange={handleChange}
                          placeholder="Enter Customer PO"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="soldToCompanyName">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToCompanyName")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Company Name</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToCompanyName"
                          value={formData.soldToCompanyName}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Company Name", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToCompanyName",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToCompanyName}
                          onChange={handleChange}
                          placeholder="Enter Company Name"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="soldToAddress">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToAddress")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Address</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToAddress"
                          value={formData.soldToAddress}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Address", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToAddress",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToAddress}
                          onChange={handleChange}
                          placeholder="Enter Address"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="soldToAddress2">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToAddress2")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Address 2</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToAddress2"
                          value={formData.soldToAddress2}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Address 2", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToAddress2",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToAddress2}
                          onChange={handleChange}
                          placeholder="Enter Address 2"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="soldToCity">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToCity")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>City</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToCity"
                          value={formData.soldToCity}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To City", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToCity",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToCity}
                          onChange={handleChange}
                          placeholder="Enter City"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="soldToState">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToState")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>State</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToState"
                          value={formData.soldToState}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To State", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToState",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToState}
                          onChange={handleChange}
                          placeholder="Enter State"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="soldToZipCode">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToZipCode")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Zip Code</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToZipCode"
                          value={formData.soldToZipCode}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Zip Code", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToZipCode",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToZipCode}
                          onChange={handleChange}
                          placeholder="Enter Zip Code"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="soldToCountry">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToCountry")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Country</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToCountry"
                          value={formData.soldToCountry}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Country", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToCountry",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToCountry}
                          onChange={handleChange}
                          placeholder="Enter Country"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Col>
            <Col className="sold-ship-ship-address">
              <Form
                onSubmit={handleSubmit}
                className="sold-ship-form container mt-4"
              >
                <Row className="mb-3">
                  {/* Ship to Address Details */}
                  <h3 className="subsection-header">Ship To</h3>
                  <Row className="mb-3">
                    <Col className="ship-to-company-field">
                      <Form.Group as={Row} controlId="shipToCompanyName">
                        <Form.Label column="md" md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("ShipToCompanyName")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Company Name</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col md={9}>
                          <Form.Control
                            type="text"
                            name="shipToCompanyName"
                            value={formData.shipToCompanyName}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Ship To Company Name", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ShipToCompanyName",
                                tableKey,
                              })
                            }
                            disabled={
                              isDisabled ||
                              (!roles.Admin && !roles.Apps && !roles.PSN)
                            }
                            className={fieldClasses.shipToCompanyName}
                            onChange={handleChange}
                            placeholder="Enter Company Name"
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                    <Col className="ship-to-international-field">
                      <Form.Group as={Row} controlId="internationalShipment">
                        <Form.Label column="md">
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("InternationalShip")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>International/ Container Shipment</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col className="pt-2">                          <Form.Check
                            type="switch"
                            id="internationalShipmentSwitch"
                            label={(formData.internationalShip === true || formData.internationalShip === "true") ? "Yes" : "No"} 
                            name="internationalShip"
                            checked={formData.internationalShip === true}
                            disabled={
                              isDisabled ||
                              (!roles.Admin && !roles.Apps && !roles.PSN)
                            }
                            className={`large-toggle ${fieldClasses.internationalShip}`}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              // Update local state immediately
                              handleChange({
                                target: {
                                  name: "internationalShip",
                                  value: newValue,
                                },
                              });
                              
                              // Call handleBlur to update the API and Redux state
                              handleBlur(
                                {
                                  target: {
                                    name: "internationalShip",
                                    value: newValue,
                                    type: "switch",
                                    dataset: {
                                      originalValue: newValue ? "yes" : "no",
                                      booleanValue: newValue
                                    }
                                  }
                                },
                                "International Shipment",
                                [],
                                {
                                  ID: formData.ID,
                                  url: apiUrl,
                                  field: "InternationalShip",
                                  tableKey,
                                  timestamp: new Date().getTime()
                                }
                              );
                            }}
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group as={Row} controlId="shipToAddress">
                        <Form.Label column="md" md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("ShipToAddress")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Address</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col md={9}>
                          <Form.Control
                            type="text"
                            name="shipToAddress"
                            value={formData.shipToAddress}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Ship To Address", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ShipToAddress",
                                tableKey,
                              })
                            }
                            disabled={
                              isDisabled ||
                              (!roles.Admin && !roles.Apps && !roles.PSN)
                            }
                            className={fieldClasses.shipToAddress}
                            onChange={handleChange}
                            placeholder="Enter Address"
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group as={Row} controlId="shipToAddress2">
                        <Form.Label column="md" md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("ShipToAddress2")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Address 2</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col md={9}>
                          <Form.Control
                            type="text"
                            name="shipToAddress2"
                            value={formData.shipToAddress2}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Ship To Address 2", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ShipToAddress2",
                                tableKey,
                              })
                            }
                            disabled={
                              isDisabled ||
                              (!roles.Admin && !roles.Apps && !roles.PSN)
                            }
                            className={fieldClasses.shipToAddress2}
                            onChange={handleChange}
                            placeholder="Enter Address 2"
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group as={Row} controlId="shipToCity">
                        <Form.Label column="md" md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("ShipToCity")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>City</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col md={9}>
                          <Form.Control
                            type="text"
                            name="shipToCity"
                            value={formData.shipToCity}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Ship To City", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ShipToCity",
                                tableKey,
                              })
                            }
                            disabled={
                              isDisabled ||
                              (!roles.Admin && !roles.Apps && !roles.PSN)
                            }
                            className={fieldClasses.shipToCity}
                            onChange={handleChange}
                            placeholder="Enter City"
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group as={Row} controlId="shipToState">
                        <Form.Label column="md" md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("ShipToState")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>State</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col md={9}>
                          <Form.Control
                            type="text"
                            name="shipToState"
                            value={formData.shipToState}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Ship To State", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ShipToState",
                                tableKey,
                              })
                            }
                            disabled={
                              isDisabled ||
                              (!roles.Admin && !roles.Apps && !roles.PSN)
                            }
                            className={fieldClasses.shipToState}
                            onChange={handleChange}
                            placeholder="Enter  State"
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group as={Row} controlId="shipToZipCode">
                        <Form.Label column="md" md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("ShipToZipCode")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Zip Code</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col md={9}>
                          <Form.Control
                            type="text"
                            name="shipToZipCode"
                            value={formData.shipToZipCode}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Ship To Zip Code", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ShipToZipCode",
                                tableKey,
                              })
                            }
                            disabled={
                              isDisabled ||
                              (!roles.Admin && !roles.Apps && !roles.PSN)
                            }
                            className={fieldClasses.shipToZipCode}
                            onChange={handleChange}
                            placeholder="Enter Zip Code"
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group as={Row} controlId="shipToCountry">
                        <Form.Label column="md" md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("ShipToCountry")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Country</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col md={9}>
                          <Form.Control
                            type="text"
                            name="shipToCountry"
                            value={formData.shipToCountry}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Ship To Country", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ShipToCountry",
                                tableKey,
                              })
                            }
                            disabled={
                              isDisabled ||
                              (!roles.Admin && !roles.Apps && !roles.PSN)
                            }
                            className={fieldClasses.shipToCountry}
                            onChange={handleChange}
                            placeholder="Enter Country"
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                </Row>
              </Form>
            </Col>
          </Row>
          {/* Ship to Sold To Contact */}
          <Row className="sold-ship-section-pair">
            <Col md={6} className="sold-ship-sold-contact">
              <Form
                onSubmit={handleSubmit}
                className="sold-ship-form container mt-4"
              >
                <Row className="mb-3">
                  <h3 className="subsection-header">Sold To Contact</h3>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="soldToFirstName">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToFirstName")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>First Name</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToFirstName"
                          value={formData.soldToFirstName}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To First Name", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToFirstName",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToFirstName}
                          onChange={handleChange}
                          placeholder="Enter First Name"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="soldToLastName">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToLastName")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Last Name</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToLastName"
                          value={formData.soldToLastName}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Last Name", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToLastName",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToLastName}
                          onChange={handleChange}
                          placeholder="Enter Last Name"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="soldToMobilePhone">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToMobilePhone")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Mobile Phone</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToMobilePhone"
                          value={formData.soldToMobilePhone}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Mobile Phone", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToMobilePhone",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToMobilePhone}
                          onChange={handleChange}
                          placeholder="Enter Mobile Phone"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="soldToPhone">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToPhone")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Phone</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToPhone"
                          value={formData.soldToPhone}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Phone", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToPhone",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToPhone}
                          onChange={handleChange}
                          placeholder="Enter Phone"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="soldToEmail">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToEmail")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Email</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToEmail"
                          value={formData.soldToEmail}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Email", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToEmail",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToEmail}
                          onChange={handleChange}
                          placeholder="Enter Email"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="soldToFax">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SoldToFax")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Fax</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="soldToFax"
                          value={formData.soldToFax}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Sold To Fax", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SoldToFax",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.soldToFax}
                          onChange={handleChange}
                          placeholder="Enter Fax"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3"></Row>
              </Form>
            </Col>
            <Col className="sold-ship-ship-contact">
              <Form
                onSubmit={handleSubmit}
                className="sold-ship-form container mt-4"
              >
                <Row className="mb-3">
                  <h3 className="subsection-header">Ship To Contact</h3>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="shipToFirstName">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ShipToFirstName")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>First Name</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="shipToFirstName"
                          value={formData.shipToFirstName}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Ship To First Name", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ShipToFirstName",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.shipToFirstName}
                          onChange={handleChange}
                          placeholder="Enter First Name"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="shipToLastName">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ShipToLastName")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Last Name</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="shipToLastName"
                          value={formData.shipToLastName}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Ship To Last Name", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ShipToLastName",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.shipToLastName}
                          onChange={handleChange}
                          placeholder="Enter Last Name"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="shipToMobilePhone">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ShipToMobilePhone")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Mobile Phone</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="shipToMobilePhone"
                          value={formData.shipToMobilePhone}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Ship To Mobile Phone", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ShipToMobilePhone",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.shipToMobilePhone}
                          onChange={handleChange}
                          placeholder="Enter Mobile Phone"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="shipToPhone">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ShipToPhone")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Phone</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="shipToPhone"
                          value={formData.shipToPhone}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Ship To Phone", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ShipToPhone",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.shipToPhone}
                          onChange={handleChange}
                          placeholder="Enter Phone"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="shipToEmail">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ShipToEmail")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Email</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="shipToEmail"
                          value={formData.shipToEmail}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Ship To Email", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ShipToEmail",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.shipToEmail}
                          onChange={handleChange}
                          placeholder="Enter Email"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="shipToFax">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ShipToFax")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Fax</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="shipToFax"
                          value={formData.shipToFax}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Ship To Fax", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ShipToFax",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.shipToFax}
                          onChange={handleChange}
                          placeholder="Enter Fax"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
          </div>
          {/* Invoice Price & Shipment Details */}
          <Row className="sold-ship-section-pair">
            <Col md={6}>
              <Form
                onSubmit={handleSubmit}
                className="sold-ship-form container mt-4"
              >
                <Row className="mb-3">
                  <h3 className="subsection-header">
                    Invoice Price Information
                  </h3>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="sellPriceAmount">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("sellPriceAmount")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Invoice Price</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="sellPriceAmount"
                          value={
                            editingField === "sellPriceAmount"
                              ? formData.sellPriceAmount
                              : formatAsCurrency(formData.sellPriceAmount)
                          }
                          onFocus={(e) => {
                            setEditingField("sellPriceAmount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.sellPriceAmount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            // Use the ID from the PriceBreakdown record for PATCH
                            const priceBreakdownID = (storedPriceBreakdown[0] && storedPriceBreakdown[0].ID) || "";
                            handleCurrencyBlur(
                              { ...e, target: { ...e.target, value: e.target.value } },
                              handleBlur,
                              { ...formData, ID: priceBreakdownID },
                              priceBreakdownApiUrl,
                              priceBreakdownTableKey,
                              "SellPriceAmount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.sellPriceAmount}
                          placeholder="Enter Invoice Price"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="paymentTerms">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("PaymentTerms")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Payment Terms</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="paymentTerms"
                          value={formData.paymentTerms}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Payment Terms", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "PaymentTerms",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.paymentTerms}
                          onChange={handleChange}
                          placeholder="Enter Payment Terms"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="specialTermsApprovedBy">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SpecialTermsApprovedBy")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Special Terms Approved By</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="specialTermsApprovedBy"
                          value={formData.specialTermsApprovedBy}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Special Terms Approved By",
                              specialTermsApprovedByOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "SpecialTermsApprovedBy",
                                tableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.specialTermsApprovedBy}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Special Terms Approved By
                          </option>
                          {specialTermsApprovedByOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="specialTermsNotes">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SpecialTermsNotes")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Special Terms Notes</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="specialTermsNotes"
                          value={formData.specialTermsNotes}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Special Terms Notes", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SpecialTermsNotes",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.specialTermsNotes}
                          onChange={handleChange}
                          placeholder="Enter Special Terms Notes"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Col>
            <Col>
              <Form
                onSubmit={handleSubmit}
                className="sold-ship-form container mt-4"
              >
                <Row className="mb-3">
                  <h3 className="subsection-header">Shipment Details</h3>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="customerCarrierCallAhead">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("CustomerCarrierCallAhead")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Customer Carrier Call Ahead</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="customerCarrierCallAhead"
                          value={formData.customerCarrierCallAhead}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Customer Carrier Call Ahead",
                              genericOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CustomerCarrierCallAhead",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.customerCarrierCallAhead}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Customer Carrier Call Ahead
                          </option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.customerCarrierCallAhead,
                            genericOptions
                          ) &&
                            formData.customerCarrierCallAhead && (
                              <option
                                value={formData.customerCarrierCallAhead}
                                style={{ color: "red" }}
                              >
                                *{formData.customerCarrierCallAhead}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="carrier">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("Carrier")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Carrier</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="carrier"
                          value={formData.carrier}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Carrier", carrierOptions, {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "Carrier",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.carrier}
                          onChange={handleChange}
                        >
                          <option value="">Select Carrier</option>
                          {carrierOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.carrier,
                            carrierOptions
                          ) &&
                            formData.carrier && (
                              <option
                                value={formData.carrier}
                                style={{ color: "red" }}
                              >
                                *{formData.carrier}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group
                      as={Row}
                      controlId="selectJobSiteServicesIncluded"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "SelectJobSiteServicesIncluded"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Select Job Site Services Included</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="selectJobSiteServicesIncluded"
                          value={formData.selectJobSiteServicesIncluded}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Select Job Site Services Included",
                              selectJobSiteServicesIncludedOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "SelectJobSiteServicesIncluded",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.selectJobSiteServicesIncluded}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Select Job Site Services Included
                          </option>
                          {selectJobSiteServicesIncludedOptions.map(
                            (option) => (
                              <option
                                key={option.ID}
                                value={option.DisplayText}
                              >
                                {option.DisplayText}
                              </option>
                            )
                          )}
                          {!isValueInDropdown(
                            formData.selectJobSiteServicesIncluded,
                            selectJobSiteServicesIncludedOptions
                          ) &&
                            formData.selectJobSiteServicesIncluded && (
                              <option
                                value={formData.selectJobSiteServicesIncluded}
                                style={{ color: "red" }}
                              >
                                *{formData.selectJobSiteServicesIncluded}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="installationServiceType">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("InstallationServiceType")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Installation Service Type</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="installationServiceType"
                          value={formData.installationServiceType}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Installation Service Type",
                              installationServiceTypeOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "InstallationServiceType",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.installationServiceType}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Installation Service Type
                          </option>
                          {installationServiceTypeOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.installationServiceType,
                            installationServiceTypeOptions
                          ) &&
                            formData.installationServiceType && (
                              <option
                                value={formData.installationServiceType}
                                style={{ color: "red" }}
                              >
                                *{formData.installationServiceType}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="notificationOfShipment">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("NotificationOfShipment")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Notification Of Shipment</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="notificationOfShipment"
                          value={formData.notificationOfShipment}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Notification Of Shipment",
                              notificationOfShipmentOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "NotificationOfShipment",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.notificationOfShipment}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Notification Of Shipment
                          </option>
                          {notificationOfShipmentOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.notificationOfShipment,
                            notificationOfShipmentOptions
                          ) &&
                            formData.notificationOfShipment && (
                              <option
                                value={formData.notificationOfShipment}
                                style={{ color: "red" }}
                              >
                                *{formData.notificationOfShipment}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="shipmentDetailsOther">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ShipmentDetailsOther")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Shipment Details Other</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="shipmentDetailsOther"
                          value={formData.shipmentDetailsOther}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Shipment Details Other", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ShipmentDetailsOther",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.shipmentDetailsOther}
                          onChange={handleChange}
                          placeholder="Enter Shipment Details Other"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="arrivalConfirmationNotes">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ArrivalConfirmationNotes")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Arrival Confirmation Notes</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="arrivalConfirmationNotes"
                          value={formData.arrivalConfirmationNotes}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Arrival Confirmation Notes", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ArrivalConfirmationNotes",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.arrivalConfirmationNotes}
                          onChange={handleChange}
                          placeholder="Enter Arrival Confirmation Notes"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="stationWeight">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("StationWeight")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Station Weight</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="stationWeight"
                          value={formData.stationWeight}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Station Weight", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "StationWeight",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.stationWeight}
                          onChange={handleChange}
                          placeholder="Enter Station Weight"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
          {/* International Shipment Details */}
          {formData.internationalShip && (
            <InternationalShip/>
                )}
        </div>)}
    </div>
  );
}

export default SoldToShipTo;
